/**
 * Reporting Core SDK - API Client
 *
 * Framework-agnostic client for the Reporting API.
 */

import type {
  ApiResponse,
  AuthProviderConfig,
  ExportMetadata,
  FilterOptionsData,
  GenerateReportRequest,
  HtmlPaginationData,
  HtmlReportResult,
  ListReportsData,
  PollOptions,
  ReportConfig,
  ReportData,
  ReportingClientConfig,
  ReportRunMetadata
} from './types';
import { ReportingApiError } from './types';

/**
 * Check if provider is a token-based auth provider.
 */
function isTokenProvider(
  provider: AuthProviderConfig
): provider is { getAccessToken: () => Promise<string> | string } {
  return 'getAccessToken' in provider;
}

/**
 * Extract pagination metadata from response headers.
 *
 * The API returns pagination info in these headers:
 * - x-report-current-page
 * - x-report-page-size
 * - x-report-total-pages
 * - x-report-total-records
 */
function extractPaginationHeaders(headers: Headers): HtmlPaginationData | null {
  const currentPage = headers.get('x-report-current-page');
  const pageSize = headers.get('x-report-page-size');
  const totalPages = headers.get('x-report-total-pages');
  const totalRecords = headers.get('x-report-total-records');

  // Return null if essential headers are missing
  if (!currentPage || !totalPages) {
    return null;
  }

  return {
    currentPage: parseInt(currentPage, 10),
    pageSize: pageSize ? parseInt(pageSize, 10) : 20,
    totalPages: parseInt(totalPages, 10),
    totalRecords: totalRecords ? parseInt(totalRecords, 10) : 0
  };
}

/**
 * Core Reporting API client.
 *
 * Wraps the user reporting endpoints with:
 * - Automatic auth header injection
 * - Response unwrapping
 * - Consistent error handling
 *
 * @example
 * ```ts
 * const client = createReportingClient({
 *   baseUrl: process.env.REACT_APP_REPORTING_API_BASE_URL,
 *   authProvider: {
 *     getAccessToken: () => myAuthStore.getToken(),
 *   },
 * });
 *
 * const reports = await client.listReports();
 * ```
 */
export interface ReportingClient {
  /**
   * List all available reports.
   * GET /api/v1/reports
   */
  listReports(): Promise<ListReportsData>;

  /**
   * Get report configuration including filters.
   * GET /api/v1/reports/{reportId}/config
   */
  getReportConfig(reportId: string): Promise<ReportConfig>;

  /**
   * Get filter options for a query-backed filter.
   * GET /api/v1/reports/{reportId}/filters/{filterId}/options
   *
   * @param reportId - Report ID
   * @param filterId - Filter ID
   * @param dependencyValues - Current values of dependency filters (for cascading)
   */
  getFilterOptions(
    reportId: string,
    filterId: string,
    dependencyValues?: Record<string, unknown>
  ): Promise<FilterOptionsData>;

  /**
   * Generate a report with the given parameters.
   * POST /api/v1/reports/{reportId}/generate
   *
   * Returns either:
   * - JSON data (format: 'html' with renderHtml: false or unset)
   * - HTML string (format: 'html' with renderHtml: true)
   * - Export metadata with download URL (format: 'csv' | 'xlsx' | 'pdf')
   */
  generateReport(
    reportId: string,
    request: GenerateReportRequest
  ): Promise<ReportData | string | ExportMetadata>;

  /**
   * Generate a report and return JSON data (convenience method).
   */
  generateReportJson(
    reportId: string,
    filters: Record<string, unknown>,
    options?: { page?: number; pageSize?: number }
  ): Promise<ReportData>;

  /**
   * Generate a report and return HTML preview (convenience method).
   */
  generateReportHtml(
    reportId: string,
    filters: Record<string, unknown>,
    options?: {
      page?: number;
      pageSize?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<string>;

  /**
   * Generate a CSV export and return download metadata (convenience method).
   */
  generateReportCsv(
    reportId: string,
    filters: Record<string, unknown>,
    options?: { pageSize?: number }
  ): Promise<ExportMetadata>;

  /**
   * Generate an XLSX export and return download metadata (convenience method).
   */
  generateReportXlsx(
    reportId: string,
    filters: Record<string, unknown>,
    options?: { pageSize?: number }
  ): Promise<ExportMetadata>;

  /**
   * Generate a PDF export and return download metadata (convenience method).
   */
  generateReportPdf(
    reportId: string,
    filters: Record<string, unknown>,
    options?: { pageSize?: number }
  ): Promise<ExportMetadata>;

  // ============================================================================
  // Async Report Run Methods
  // ============================================================================

  /**
   * Generate a report asynchronously.
   * Returns immediately with run metadata for polling.
   * POST /api/v1/reports/{reportId}/generate with executionMode: 'async'
   */
  generateReportAsync(
    reportId: string,
    request: Omit<GenerateReportRequest, 'executionMode'>
  ): Promise<ReportRunMetadata>;

  /**
   * Get the status of an async report run.
   * GET /api/v1/reports/runs/{runId}
   */
  getReportRunStatus(runId: string): Promise<ReportRunMetadata>;

  /**
   * Get the result of a completed async report run.
   * GET /api/v1/reports/runs/{runId}/result
   * Only for data/html runs (not exports).
   */
  getReportRunResult(runId: string): Promise<ReportData | string>;

  /**
   * Poll an async run until completion and return the final result.
   *
   * For export runs, returns the run metadata with downloadUrl.
   * For data/html runs, fetches and returns the result.
   */
  pollReportRunUntilComplete(
    runId: string,
    options?: PollOptions
  ): Promise<ReportData | string | ReportRunMetadata>;

  /**
   * Generate a report asynchronously and wait for completion.
   * Combines generateReportAsync + pollReportRunUntilComplete.
   *
   * This is the easiest way to generate large reports that might
   * exceed synchronous timeout limits.
   */
  generateReportAsyncAndWait(
    reportId: string,
    request: Omit<GenerateReportRequest, 'executionMode'>,
    options?: PollOptions
  ): Promise<ReportData | string | ReportRunMetadata>;

  /**
   * Download a file from the API with authentication.
   * Use this to download export files that require auth headers.
   *
   * @param url - The full URL or relative path to download
   * @param filename - The filename to save as
   */
  downloadFile(url: string, filename: string): Promise<void>;
}

/**
 * Create a new Reporting API client.
 */
export function createReportingClient(
  config: ReportingClientConfig
): ReportingClient {
  if (!config.baseUrl) {
    throw new Error(
      'ReportingClient: baseUrl is required. Set REACT_APP_REPORTING_API_BASE_URL environment variable.'
    );
  }
  const baseUrl = config.baseUrl;
  const authProvider = config.authProvider;
  const onUnauthorized = config.onUnauthorized;

  /**
   * Get auth headers from the provider.
   */
  async function getAuthHeaders(): Promise<Record<string, string>> {
    if (isTokenProvider(authProvider)) {
      const token = await authProvider.getAccessToken();
      return { Authorization: `Bearer ${token}` };
    }
    return await authProvider.getAuthHeaders();
  }

  /**
   * Make an authenticated request and unwrap the response.
   * Automatically retries once on 401 Unauthorized by refreshing the token.
   */
  async function request<T>(
    endpoint: string,
    options: RequestInit = {},
    acceptedStatusCodes: number[] = [200],
    isRetry: boolean = false
  ): Promise<T> {
    const authHeaders = await getAuthHeaders();

    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers
      }
    });

    // Handle 401 Unauthorized - attempt token refresh and retry
    if (response.status === 401 && !isRetry) {
      // Token may have expired - notify the auth system to invalidate cached token
      if (onUnauthorized) {
        onUnauthorized();
      }
      // Retry the request - getAuthHeaders will fetch a fresh token
      return request<T>(endpoint, options, acceptedStatusCodes, true);
    }

    // Handle HTML responses (for renderHtml: true)
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('text/html')) {
      if (!response.ok) {
        throw new ReportingApiError('Request failed', {
          statusCode: response.status
        });
      }

      // Extract pagination metadata from response headers
      const html = await response.text();
      const pagination = extractPaginationHeaders(response.headers);

      // Return HtmlReportResult with pagination data
      const result: HtmlReportResult = { html, pagination };
      return (result as unknown) as T;
    }

    // Parse JSON response
    let body: ApiResponse<T>;
    try {
      body = await response.json();
    } catch {
      throw new ReportingApiError('Failed to parse response', {
        statusCode: response.status
      });
    }

    // Handle error responses
    // Accept if status is in accepted list OR response.ok is true (for compatibility with tests)
    const isAccepted =
      acceptedStatusCodes.includes(response.status) ||
      (response.ok && acceptedStatusCodes.includes(200));
    if (!isAccepted || body.success === false) {
      const errorBody = body as {
        success: false;
        error: string;
        message: string;
        details?: Record<string, unknown>;
      };
      throw new ReportingApiError(errorBody.message || 'Request failed', {
        statusCode: response.status,
        errorCode: errorBody.error,
        details: errorBody.details
      });
    }

    // Unwrap success response
    return body.data;
  }

  /**
   * Sleep for a given duration.
   */
  function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  return {
    async listReports(): Promise<ListReportsData> {
      return request<ListReportsData>('/reports');
    },

    async getReportConfig(reportId: string): Promise<ReportConfig> {
      return request<ReportConfig>(
        `/reports/${encodeURIComponent(reportId)}/config`
      );
    },

    async getFilterOptions(
      reportId: string,
      filterId: string,
      dependencyValues?: Record<string, unknown>
    ): Promise<FilterOptionsData> {
      let endpoint = `/reports/${encodeURIComponent(
        reportId
      )}/filters/${encodeURIComponent(filterId)}/options`;

      // Add dependency values as query parameters
      if (dependencyValues && Object.keys(dependencyValues).length > 0) {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(dependencyValues)) {
          if (value !== undefined && value !== null) {
            params.set(key, String(value));
          }
        }
        const queryString = params.toString();
        if (queryString) {
          endpoint += `?${queryString}`;
        }
      }

      return request<FilterOptionsData>(endpoint);
    },

    async generateReport(
      reportId: string,
      generateRequest: GenerateReportRequest
    ): Promise<ReportData | string | ExportMetadata> {
      return request<ReportData | string | ExportMetadata>(
        `/reports/${encodeURIComponent(reportId)}/generate`,
        {
          method: 'POST',
          body: JSON.stringify(generateRequest)
        }
      );
    },

    async generateReportJson(
      reportId: string,
      filters: Record<string, unknown>,
      options?: { page?: number; pageSize?: number }
    ): Promise<ReportData> {
      const result = await this.generateReport(reportId, {
        filters,
        format: 'html',
        renderHtml: false,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? 50
      });

      if (typeof result === 'string') {
        throw new ReportingApiError('Unexpected HTML response', {
          errorCode: 'UNEXPECTED_RESPONSE'
        });
      }

      return result as ReportData;
    },

    async generateReportHtml(
      reportId: string,
      filters: Record<string, unknown>,
      options?: {
        page?: number;
        pageSize?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
      }
    ): Promise<string> {
      const result = await this.generateReport(reportId, {
        filters,
        format: 'html',
        renderHtml: true,
        page: options?.page ?? 1,
        pageSize: options?.pageSize ?? 50,
        sortBy: options?.sortBy,
        sortOrder: options?.sortOrder
      });

      // Handle HtmlReportResult (new format with pagination)
      const htmlResult = (result as unknown) as HtmlReportResult;
      if (htmlResult && typeof htmlResult.html === 'string') {
        return htmlResult.html;
      }

      // Fallback for legacy string response
      if (typeof result === 'string') {
        return result;
      }

      throw new ReportingApiError('Expected HTML response', {
        errorCode: 'UNEXPECTED_RESPONSE'
      });
    },

    async generateReportCsv(
      reportId: string,
      filters: Record<string, unknown>,
      options?: { pageSize?: number }
    ): Promise<ExportMetadata> {
      const result = await this.generateReport(reportId, {
        filters,
        format: 'csv',
        page: 1,
        pageSize: options?.pageSize ?? 10000
      });

      if (typeof result === 'string') {
        throw new ReportingApiError('Unexpected HTML response for CSV export', {
          errorCode: 'UNEXPECTED_RESPONSE'
        });
      }

      // Export response includes downloadUrl at top level
      const exportResult = result as ExportMetadata;
      if (!exportResult.downloadUrl) {
        throw new ReportingApiError(
          'Missing download URL in CSV export response',
          {
            errorCode: 'MISSING_DOWNLOAD_URL'
          }
        );
      }

      return exportResult;
    },

    async generateReportXlsx(
      reportId: string,
      filters: Record<string, unknown>,
      options?: { pageSize?: number }
    ): Promise<ExportMetadata> {
      const result = await this.generateReport(reportId, {
        filters,
        format: 'xlsx',
        page: 1,
        pageSize: options?.pageSize ?? 10000
      });

      if (typeof result === 'string') {
        throw new ReportingApiError(
          'Unexpected HTML response for XLSX export',
          {
            errorCode: 'UNEXPECTED_RESPONSE'
          }
        );
      }

      // Export response includes downloadUrl at top level
      const exportResult = result as ExportMetadata;
      if (!exportResult.downloadUrl) {
        throw new ReportingApiError(
          'Missing download URL in XLSX export response',
          {
            errorCode: 'MISSING_DOWNLOAD_URL'
          }
        );
      }

      return exportResult;
    },

    async generateReportPdf(
      reportId: string,
      filters: Record<string, unknown>,
      options?: { pageSize?: number }
    ): Promise<ExportMetadata> {
      const result = await this.generateReport(reportId, {
        filters,
        format: 'pdf',
        page: 1,
        pageSize: options?.pageSize ?? 10000
      });

      if (typeof result === 'string') {
        throw new ReportingApiError('Unexpected HTML response for PDF export', {
          errorCode: 'UNEXPECTED_RESPONSE'
        });
      }

      // Export response includes downloadUrl at top level
      const exportResult = result as ExportMetadata;
      if (!exportResult.downloadUrl) {
        throw new ReportingApiError(
          'Missing download URL in PDF export response',
          {
            errorCode: 'MISSING_DOWNLOAD_URL'
          }
        );
      }

      return exportResult;
    },

    // =========================================================================
    // Async Report Run Methods
    // =========================================================================

    async generateReportAsync(
      reportId: string,
      generateRequest: Omit<GenerateReportRequest, 'executionMode'>
    ): Promise<ReportRunMetadata> {
      return request<ReportRunMetadata>(
        `/reports/${encodeURIComponent(reportId)}/generate`,
        {
          method: 'POST',
          body: JSON.stringify({
            ...generateRequest,
            executionMode: 'async'
          })
        },
        [202] // Accept 202 Accepted
      );
    },

    async getReportRunStatus(runId: string): Promise<ReportRunMetadata> {
      return request<ReportRunMetadata>(
        `/reports/runs/${encodeURIComponent(runId)}`
      );
    },

    async getReportRunResult(runId: string): Promise<ReportData | string> {
      return request<ReportData | string>(
        `/reports/runs/${encodeURIComponent(runId)}/result`
      );
    },

    async pollReportRunUntilComplete(
      runId: string,
      options?: PollOptions
    ): Promise<ReportData | string | ReportRunMetadata> {
      const pollIntervalMs = options?.pollIntervalMs ?? 2000;
      const maxWaitMs = options?.maxWaitMs ?? 600000; // 10 minutes
      const startTime = Date.now();

      let lastStatus: ReportRunMetadata | null = null;

      while (Date.now() - startTime < maxWaitMs) {
        const status = await this.getReportRunStatus(runId);
        lastStatus = status;

        // Notify callback if provided
        options?.onStatusChange?.(status);

        // Check for terminal states
        if (status.status === 'succeeded') {
          // For export runs, return the metadata with downloadUrl
          if (status.downloadUrl) {
            // Prepend origin (baseUrl without path) if downloadUrl is a relative path
            let fullDownloadUrl = status.downloadUrl;
            if (!status.downloadUrl.startsWith('http')) {
              // Extract origin from baseUrl (e.g., https://domain.com from https://domain.com/api/v1)
              const urlObj = new URL(baseUrl);
              fullDownloadUrl = `${urlObj.origin}${status.downloadUrl}`;
            }
            return { ...status, downloadUrl: fullDownloadUrl };
          }
          // For data/html runs, fetch the result
          return this.getReportRunResult(runId);
        }

        if (status.status === 'failed') {
          throw new ReportingApiError(
            status.error?.message ?? 'Report run failed',
            {
              errorCode: status.error?.code ?? 'REPORT_RUN_FAILED',
              details: status.error?.details
            }
          );
        }

        // Wait before polling again
        await sleep(pollIntervalMs);
      }

      // Timeout - throw error with last known status
      throw new ReportingApiError(
        'Report run timed out waiting for completion',
        {
          errorCode: 'POLL_TIMEOUT',
          details: {
            runId,
            lastStatus: lastStatus?.status,
            maxWaitMs
          }
        }
      );
    },

    async generateReportAsyncAndWait(
      reportId: string,
      generateRequest: Omit<GenerateReportRequest, 'executionMode'>,
      options?: PollOptions
    ): Promise<ReportData | string | ReportRunMetadata> {
      const run = await this.generateReportAsync(reportId, generateRequest);
      return this.pollReportRunUntilComplete(run.runId, options);
    },

    async downloadFile(url: string, filename: string): Promise<void> {
      const authHeaders = await getAuthHeaders();
      const urlObj = new URL(baseUrl);
      const apiOrigin = urlObj.origin;

      // Determine if this is an API Gateway URL (needs to fetch JSON for pre-signed URL) or pre-signed S3 URL (direct download)
      let finalDownloadUrl: string;

      if (url.startsWith('http')) {
        // Absolute URL - check if it's from our API Gateway or external (pre-signed S3)
        const downloadUrlObj = new URL(url);
        if (downloadUrlObj.origin === apiOrigin) {
          // Same origin as our API Gateway - fetch with Accept: application/json to get pre-signed S3 URL
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              ...authHeaders,
              Accept: 'application/json'
            }
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new ReportingApiError(
              errorData.message || 'Failed to fetch download URL',
              { statusCode: response.status, details: errorData }
            );
          }

          const result = await response.json();
          const presignedUrl = result.data?.downloadUrl || result.downloadUrl;

          if (!presignedUrl) {
            throw new ReportingApiError('No download URL in response');
          }

          finalDownloadUrl = presignedUrl;
        } else {
          // Different origin (pre-signed S3 URL) - use directly
          finalDownloadUrl = url;
        }
      } else {
        // Relative URL - resolve to API Gateway and fetch JSON for pre-signed URL
        const resolvedUrl = `${apiOrigin}${url}`;
        const response = await fetch(resolvedUrl, {
          method: 'GET',
          headers: {
            ...authHeaders,
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new ReportingApiError(
            errorData.message || 'Failed to fetch download URL',
            { statusCode: response.status, details: errorData }
          );
        }

        const result = await response.json();
        const presignedUrl = result.data?.downloadUrl || result.downloadUrl;

        if (!presignedUrl) {
          throw new ReportingApiError('No download URL in response');
        }

        finalDownloadUrl = presignedUrl;
      }

      // Trigger download using anchor element (works for pre-signed S3 URLs)
      const link = document.createElement('a');
      link.href = finalDownloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
}
